/**
 * Header Component
 */
export class Header {
    constructor() {
        this.header = null;
        this.nav = null;
        this.drawer = null;
        this.timeline = null;
        this.drawerTimeline = null;
        this.isMainPage = false;
        this.isNavHovering = false;
        this.isScrolled = false;
        this.scrollThreshold = 40;
        this._scrollRaf = null;
    }

    init() {
        this.header = document.getElementById("main-header");
        if (!this.header) return;

        // Main page detection: hero exists on the home page (Swiper or Glass)
        this.isMainPage = Boolean(
            document.querySelector(".no-main-hero") ||
                document.querySelector(".main-glass-hero")
        );

        this.nav = this.header.querySelector(".no-header__nav");
        if (this.nav) {
            this.initNavHover();
        }

        this.drawer = document.getElementById("main-drawer");
        if (this.drawer) {
            this.initDrawer();
        }

        this.initMegaMenu();
        this.initHeaderTheme();
        this.initResizeHandler();
        this.initHeaderEntranceAnimation();
    }

    /**
     * 헤더 진입 애니메이션: GSAP timeline + 텍스트 스플릿(문자 단위, SplitText 스타일)
     */
    initHeaderEntranceAnimation() {
        if (typeof gsap === "undefined") return;

        const logo = this.header.querySelector(".no-header__logo");
        const navLinks = this.header.querySelectorAll(".no-header__nav-link");
        const cta = this.header.querySelector(".no-header__cta");
        const allChars = [];

        gsap.set(logo, { opacity: 0, y: -10 });
        gsap.set(cta, { opacity: 0, y: -8 });

        // 데스크톱: 네비 텍스트를 문자 단위로 스플릿 (SplitText 스타일)
        if (navLinks.length && window.innerWidth > 1024) {
            navLinks.forEach((link) => {
                const textSpan = link.querySelector("span:first-child");
                if (!textSpan || textSpan.classList.contains("is-split")) return;
                const text = textSpan.textContent || "";
                if (!text) return;
                textSpan.classList.add("is-split");
                textSpan.textContent = "";
                text.split("").forEach((c) => {
                    const s = document.createElement("span");
                    s.className = "no-header__nav-char";
                    s.textContent = c;
                    textSpan.appendChild(s);
                    allChars.push(s);
                });
            });
        } else {
            gsap.set(navLinks, { opacity: 0, y: -6 });
        }

        if (allChars.length) gsap.set(allChars, { opacity: 0, y: 6 });

        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

        tl.to(logo, { opacity: 1, y: 0, duration: 0.45 })
            .to(
                allChars.length ? allChars : navLinks,
                {
                    opacity: 1,
                    y: 0,
                    duration: allChars.length ? 0.28 : 0.32,
                    stagger: allChars.length ? 0.018 : 0.05,
                    ease: "power2.out",
                },
                "-=0.28"
            )
            .to(cta, { opacity: 1, y: 0, duration: 0.35 }, "-=0.2");
    }

    /**
     * 데스크톱 메가 메뉴 (data-mega-trigger, .no-header__mega) 열기/닫기
     */
    initMegaMenu() {
        const triggers = this.header.querySelectorAll("[data-mega-trigger]");
        const megaPanels = this.header.querySelectorAll(".no-header__mega");
        if (!triggers.length || !megaPanels.length) return;

        let closeTimer = null;

        const setActive = (slug) => {
            megaPanels.forEach((panel) => {
                panel.classList.toggle("is-active", panel.dataset.menu === slug);
            });
            triggers.forEach((trigger) => {
                trigger.classList.toggle(
                    "is-hover",
                    trigger.dataset.megaTrigger === slug
                );
            });
            this.header.classList.toggle("no-header--mega-open", Boolean(slug));
            this.updateHeaderAppearance();
        };

        const close = () => setActive("");

        const scheduleClose = () => {
            if (closeTimer) clearTimeout(closeTimer);
            closeTimer = setTimeout(close, 200);
        };

        const bottom = this.header.querySelector(".no-header__bottom");
        if (bottom) {
            bottom.addEventListener("mouseenter", () => {
                if (closeTimer) clearTimeout(closeTimer);
            });
            bottom.addEventListener("mouseleave", scheduleClose);
        }

        // 오버레이 위로 마우스가 가면(헤더/메가에서 아래로 내려간 경우) 메뉴 닫기
        const overlay = document.getElementById("header-overlay");
        if (overlay) {
            overlay.addEventListener("mouseenter", scheduleClose);
            overlay.addEventListener("click", close);
        }

        triggers.forEach((trigger) => {
            const slug = trigger.dataset.megaTrigger;
            trigger.addEventListener("mouseenter", () => {
                if (closeTimer) clearTimeout(closeTimer);
                setActive(slug);
            });
            trigger.addEventListener("focus", () => setActive(slug));
            trigger.addEventListener("mouseleave", scheduleClose);
            trigger.addEventListener("blur", scheduleClose);
        });

        megaPanels.forEach((panel) => {
            panel.addEventListener("mouseenter", () => {
                if (closeTimer) clearTimeout(closeTimer);
            });
            panel.addEventListener("mouseleave", scheduleClose);
        });
    }

    initHeaderTheme() {
        // Sub pages: always solid. Main page: transparent until scroll, but solid on nav hover.
        const onScroll = () => {
            if (this._scrollRaf) return;
            this._scrollRaf = requestAnimationFrame(() => {
                this._scrollRaf = null;
                const y = window.scrollY || window.pageYOffset || 0;
                this.isScrolled = y > this.scrollThreshold;
                this.updateHeaderAppearance();
            });
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        // initial
        onScroll();
        this.updateHeaderAppearance();
    }

    updateHeaderAppearance() {
        const megaOpen = this.header.classList.contains("no-header--mega-open");
        const shouldBeSolid =
            !this.isMainPage ||
            this.isScrolled ||
            this.isNavHovering ||
            megaOpen;
        this.header.classList.toggle("no-header--solid", shouldBeSolid);
    }

    initNavHover() {
        this.timeline = gsap.timeline({ paused: true });
        this.timeline.to(this.header, {
            height: () => {
                return this.nav.offsetHeight + "px";
            },
            duration: 0.4,
            ease: "power3.inOut",
        });

        const onEnter = () => {
            this.header.setAttribute("aria-expanded", true);
            // Main page: hovering nav should switch to solid theme
            this.isNavHovering = true;
            this.updateHeaderAppearance();
            this.timeline.play();
        };

        const onLeave = () => {
            this.header.removeAttribute("aria-expanded");
            this.isNavHovering = false;
            this.updateHeaderAppearance();
            this.timeline.reverse();
        };

        this.nav.addEventListener("mouseenter", onEnter);
        this.header.addEventListener("mouseleave", onLeave);
    }

    initDrawer() {
        const drawerOpenBtn = this.header.querySelector(".no-header__toggle");
        const drawerCloseBtn = this.drawer.querySelector(".no-drawer__toggle");

        if (!drawerOpenBtn || !drawerCloseBtn) return;

        // 초기 상태 설정 (위로 숨김)
        gsap.set(this.drawer, {
            y: "-100%",
            opacity: 0,
            visibility: "hidden",
            pointerEvents: "none",
        });

        this.drawerTimeline = gsap.timeline({ paused: true });
        this.drawerTimeline.to(this.drawer, {
            y: "0%",
            opacity: 1,
            visibility: "visible",
            pointerEvents: "auto",
            ease: "power3.inOut",
            duration: 0.4,
        });

        const onOpen = () => {
            document.body.classList.add("--hidden");
            drawerOpenBtn.setAttribute("aria-expanded", "true");
            this.drawerTimeline.play();
        };

        const onClose = () => {
            document.body.classList.remove("--hidden");
            drawerOpenBtn.setAttribute("aria-expanded", "false");
            this.drawerTimeline.reverse();
            // 드로어 닫을 때 모든 서브메뉴 닫기
            this.closeAllSubmenus();
        };

        this._closeDrawer = onClose;
        this._drawerOpenBtn = drawerOpenBtn;

        drawerOpenBtn.addEventListener("click", onOpen);
        drawerCloseBtn.addEventListener("click", onClose);

        // 모바일에서만 서브메뉴 드롭다운 기능 초기화
        this.initDrawerSubmenu();
    }

    initDrawerSubmenu() {
        const submenuToggles = this.drawer.querySelectorAll(
            "[data-submenu-toggle]"
        );

        if (!submenuToggles.length) return;

        submenuToggles.forEach((toggle) => {
            toggle.addEventListener("click", (e) => {
                const gnbItem = toggle.closest(".no-drawer__gnb-item");
                if (!gnbItem) return;

                const hasSubmenu = gnbItem.querySelector(".no-drawer__submenu");

                // 하위메뉴가 없으면 기본 링크 동작 유지
                if (!hasSubmenu) {
                    return;
                }

                // 하위메뉴가 있으면 클릭 시 펼치기만 하고 링크 이동은 막기
                e.preventDefault();
                e.stopPropagation();

                const isOpen = gnbItem.classList.contains("--open");

                // 다른 모든 서브메뉴 닫기
                const allOpenItems = this.drawer.querySelectorAll(
                    ".no-drawer__gnb-item.--open"
                );
                allOpenItems.forEach((item) => {
                    if (item !== gnbItem) {
                        item.classList.remove("--open");
                        const link = item.querySelector(".no-drawer__gnb-link");
                        if (link) {
                            link.classList.remove("no-drawer__gnb-link--open");
                        }
                    }
                });

                // 현재 항목 토글
                if (isOpen) {
                    gnbItem.classList.remove("--open");
                    toggle.classList.remove("no-drawer__gnb-link--open");
                } else {
                    gnbItem.classList.add("--open");
                    toggle.classList.add("no-drawer__gnb-link--open");
                }
            });
        });
    }

    closeAllSubmenus() {
        const openItems = this.drawer.querySelectorAll(
            ".no-drawer__gnb-item.--open"
        );
        openItems.forEach((item) => {
            item.classList.remove("--open");
            const link = item.querySelector(".no-drawer__gnb-link");
            if (link) {
                link.classList.remove("no-drawer__gnb-link--open");
            }
        });
    }

    /**
     * 리사이즈 시 헤더 확장/메가 메뉴 상태 초기화 (드롭다운 위치 틀어짐 방지)
     */
    resetHeaderExpandState() {
        if (!this.header) return;
        this.header.removeAttribute("style");
        this.header.removeAttribute("aria-expanded");
        this.isNavHovering = false;
        if (this.timeline) {
            this.timeline.progress(0);
            this.timeline.pause();
        }
        this.header.classList.remove("no-header--mega-open");
        this.header.querySelectorAll(".no-header__mega").forEach((el) => el.classList.remove("is-active"));
        this.header.querySelectorAll("[data-mega-trigger]").forEach((el) => el.classList.remove("is-hover"));
        this.updateHeaderAppearance();
    }

    initResizeHandler() {
        const DESKTOP_BREAKPOINT = 1024;
        let resizeTick = null;

        window.addEventListener("resize", () => {
            if (resizeTick) clearTimeout(resizeTick);
            resizeTick = setTimeout(() => {
                resizeTick = null;
                // 리사이즈 시 항상 헤더 높이/메가 메뉴 초기화 (줄였다가 늘렸을 때 드롭다운 위치 불일치 방지)
                this.resetHeaderExpandState();
            }, 100);
        });

        window.addEventListener("resize", () => {
            // PC 뷰포트로 리사이즈 시 모바일 드로어 자동 닫기
            if (
                this.drawer &&
                this._closeDrawer &&
                this._drawerOpenBtn &&
                window.innerWidth > DESKTOP_BREAKPOINT &&
                this._drawerOpenBtn.getAttribute("aria-expanded") === "true"
            ) {
                this._closeDrawer();
            }
        });
    }
}
